import React from 'react';
import './Pages/PagesCss/LegalInformation.css';

const Disclaimer = () => {
  return (
    <div className="privacy-policy">
      {/* Header Section */}
      <section className="policy-header">
        <div className="container">
          <div className="header-content">
            <h1>Disclaimer</h1>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            <p className="header-description">
              Please read this Disclaimer carefully before using Font-Max's text processing tools and services. 
              This document outlines important limitations and responsibilities for users of our platform.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="policy-content">
        <div className="container-one">
          <div className="policy-grid">
            {/* Table of Contents */}
            <aside className="table-of-contents">
              <h3>Table of Contents</h3>
              <div>
                <ul>
                  <li><a href="#general-disclaimer">General Disclaimer</a></li>
                  <li><a href="#service-limitations">Service Limitations</a></li>
                  <li><a href="#accuracy-liability">Accuracy & Liability</a></li>
                  <li><a href="#professional-advice">Professional Advice</a></li>
                  <li><a href="#third-party-content">Third-Party Content</a></li>
                  <li><a href="#user-responsibility">User Responsibility</a></li>
                  <li><a href="#technical-disclaimer">Technical Disclaimer</a></li>
                  <li><a href="#financial-disclaimer">Financial Disclaimer</a></li>
                  <li><a href="#external-links">External Links</a></li>
                  <li><a href="#jurisdictional">Jurisdictional Notice</a></li>
                  <li><a href="#contact-us">Contact Us</a></li>
                </ul>
              </div>
            </aside>

            {/* Disclaimer Sections */}
            <div className="policy-sections">
              <section id="general-disclaimer" className="policy-section">
                <h2>1. General Disclaimer</h2>
                <div className="section-content">
                  <p>
                    The information and services provided by Font-Max (available at <a href="https://font-max.com" target="_blank" rel="noopener noreferrer">https://font-max.com</a>) 
                    are provided on an "as is" and "as available" basis. D-Tech Studios makes no representations 
                    or warranties of any kind, express or implied, regarding the completeness, accuracy, 
                    reliability, suitability, or availability of the website or the information, products, 
                    services, or related graphics contained on the website for any purpose.
                  </p>
                  <div className="warning-box">
                    <strong>Important:</strong> Any reliance you place on such information or tools is strictly at your own risk.
                  </div>
                </div>
              </section>

              <section id="service-limitations" className="policy-section">
                <h2>2. Service Limitations</h2>
                <div className="section-content">
                  <h3>2.1 Tool Accuracy</h3>
                  <p>
                    While we strive to provide accurate and reliable text processing tools, we cannot guarantee:
                  </p>
                  <ul>
                    <li>Perfect accuracy in all conversions and transformations</li>
                    <li>Error-free operation under all circumstances</li>
                    <li>Consistent results across all browsers and devices</li>
                    <li>Compatibility with all input formats and character sets</li>
                  </ul>

                  <h3>2.2 Service Availability</h3>
                  <p>
                    We do not warrant that our services will be:
                  </p>
                  <ul>
                    <li>Uninterrupted or available at all times</li>
                    <li>Free from errors, bugs, or technical issues</li>
                    <li>Secure from unauthorized access or attacks</li>
                    <li>Compatible with all software or hardware configurations</li>
                  </ul>

                  <h3>2.3 Tool Specific Limitations</h3>
                  <p>Certain tools have specific limitations:</p>
                  <ul>
                    <li><strong>Font Generators:</strong> Generated fonts may not display correctly on all platforms</li>
                    <li><strong>Text Converters:</strong> Complex formatting may not be preserved perfectly</li>
                    <li><strong>Analyzers:</strong> Results are estimates and should be verified independently</li>
                    <li><strong>Encoders/Decoders:</strong> Some encodings may have platform-specific variations</li>
                  </ul>
                </div>
              </section>

              <section id="accuracy-liability" className="policy-section">
                <h2>3. Accuracy & Liability</h2>
                <div className="section-content">
                  <h3>3.1 No Warranty</h3>
                  <p>
                    To the fullest extent permitted by applicable law, D-Tech Studios disclaims all warranties, 
                    express or implied, including but not limited to:
                  </p>
                  <ul>
                    <li>Implied warranties of merchantability</li>
                    <li>Fitness for a particular purpose</li>
                    <li>Non-infringement of intellectual property rights</li>
                    <li>Accuracy, reliability, or timeliness of information</li>
                  </ul>

                  <h3>3.2 Limitation of Liability</h3>
                  <p>
                    In no event shall D-Tech Studios, its directors, employees, partners, agents, suppliers, 
                    or affiliates be liable for any indirect, incidental, special, consequential, or punitive 
                    damages, including without limitation:
                  </p>
                  <ul>
                    <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
                    <li>Damages resulting from your access to or use of or inability to access or use the service</li>
                    <li>Damages resulting from any conduct or content of any third party on the service</li>
                    <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                    <li>Any errors or omissions in any content or for any loss or damage incurred as a result</li>
                  </ul>

                  <h3>3.3 Maximum Liability</h3>
                  <p>
                    Our total liability to you for any claim arising out of or relating to these terms or 
                    our services shall not exceed the amount you have paid us (if any) in the past six months, 
                    or $50, whichever is greater.
                  </p>
                </div>
              </section>

              <section id="professional-advice" className="policy-section">
                <h2>4. Professional Advice Disclaimer</h2>
                <div className="section-content">
                  <h3>4.1 Not Professional Advice</h3>
                  <p>
                    The tools and information provided by Font-Max are for general informational and 
                    educational purposes only. They do not constitute:
                  </p>
                  <ul>
                    <li>Legal advice or legal opinion</li>
                    <li>Financial or investment advice</li>
                    <li>Medical or health advice</li>
                    <li>Professional technical or engineering advice</li>
                    <li>Academic or educational accreditation</li>
                  </ul>

                  <h3>4.2 Independent Verification</h3>
                  <p>
                    Users should always seek appropriate professional advice for specific situations. 
                    Our tools should not be relied upon for:
                  </p>
                  <ul>
                    <li>Making legal decisions or drafting legal documents</li>
                    <li>Financial planning or investment decisions</li>
                    <li>Medical diagnoses or treatment plans</li>
                    <li>Critical business decisions without independent verification</li>
                    <li>Academic work without proper citation and verification</li>
                  </ul>

                  <div className="note-box">
                    <strong>Note:</strong> Always consult qualified professionals for advice tailored to your specific circumstances.
                  </div>
                </div>
              </section>

              <section id="third-party-content" className="policy-section">
                <h2>5. Third-Party Content & Services</h2>
                <div className="section-content">
                  <h3>5.1 External Content</h3>
                  <p>
                    Our website may contain content, links, or references to third-party websites, services, 
                    or resources. We:
                  </p>
                  <ul>
                    <li>Do not endorse and are not responsible for third-party content</li>
                    <li>Cannot control the accuracy, legality, or decency of third-party material</li>
                    <li>Are not liable for any damage or loss caused by third-party services</li>
                    <li>Do not guarantee the availability of third-party resources</li>
                  </ul>

                  <h3>5.2 User Discretion</h3>
                  <p>
                    Your interactions with third-party services are solely between you and the third party. 
                    We recommend that you:
                  </p>
                  <ul>
                    <li>Review the terms and policies of any third-party services</li>
                    <li>Exercise caution when providing personal information to third parties</li>
                    <li>Verify the credibility and security of external services</li>
                  </ul>
                </div>
              </section>

              <section id="user-responsibility" className="policy-section">
                <h2>6. User Responsibility</h2>
                <div className="section-content">
                  <h3>6.1 Content Verification</h3>
                  <p>
                    You are solely responsible for:
                  </p>
                  <ul>
                    <li>Verifying the accuracy of results generated by our tools</li>
                    <li>Ensuring processed content complies with applicable laws</li>
                    <li>Checking for errors or inconsistencies in output</li>
                    <li>Backing up important data before processing</li>
                  </ul>

                  <h3>6.2 Legal Compliance</h3>
                  <p>
                    You are responsible for ensuring that your use of our services:
                  </p>
                  <ul>
                    <li>Complies with all applicable local, state, national, and international laws</li>
                    <li>Respects intellectual property rights and copyright laws</li>
                    <li>Does not violate any third-party rights</li>
                    <li>Adheres to industry-specific regulations and standards</li>
                  </ul>

                  <h3>6.3 Risk Assumption</h3>
                  <p>
                    By using our services, you acknowledge and accept that:
                  </p>
                  <ul>
                    <li>You use our tools at your own risk</li>
                    <li>You are responsible for any consequences of using generated content</li>
                    <li>You should independently verify critical results</li>
                    <li>You maintain backup copies of important data</li>
                  </ul>
                </div>
              </section>

              <section id="technical-disclaimer" className="policy-section">
                <h2>7. Technical Disclaimer</h2>
                <div className="section-content">
                  <h3>7.1 Browser & Device Compatibility</h3>
                  <p>
                    Our tools are designed to work with modern web browsers, but we cannot guarantee:
                  </p>
                  <ul>
                    <li>Compatibility with all browser versions or configurations</li>
                    <li>Consistent performance across all devices and screen sizes</li>
                    <li>Proper functionality with browser extensions or plugins</li>
                    <li>Support for outdated or deprecated technologies</li>
                  </ul>

                  <h3>7.2 Internet & Connectivity</h3>
                  <p>
                    Service performance may be affected by factors beyond our control, including:
                  </p>
                  <ul>
                    <li>Internet connectivity and bandwidth limitations</li>
                    <li>Network congestion and latency issues</li>
                    <li>Internet service provider restrictions</li>
                    <li>Regional internet infrastructure limitations</li>
                  </ul>

                  <h3>7.3 Data Processing</h3>
                  <p>
                    While we prioritize local processing, some operations may require server-side processing. 
                    We cannot guarantee:
                  </p>
                  <ul>
                    <li>Immediate processing of large text volumes</li>
                    <li>Preservation of all formatting and special characters</li>
                    <li>Handling of extremely complex or malformed input</li>
                  </ul>
                </div>
              </section>

              <section id="financial-disclaimer" className="policy-section">
                <h2>8. Financial Disclaimer</h2>
                <div className="section-content">
                  <h3>8.1 Free Service</h3>
                  <p>
                    Font-Max is primarily a free service. We reserve the right to:
                  </p>
                  <ul>
                    <li>Modify, suspend, or discontinue any tool or feature</li>
                    <li>Implement premium features or subscription models in the future</li>
                    <li>Change pricing structures for any premium services</li>
                    <li>Limit access to certain tools or features</li>
                  </ul>

                  <h3>8.2 No Financial Advice</h3>
                  <p>
                    Nothing on our website should be construed as:
                  </p>
                  <ul>
                    <li>Investment advice or financial recommendation</li>
                    <li>Endorsement of any financial product or service</li>
                    <li>Guarantee of financial results or outcomes</li>
                    <li>Substitute for professional financial consultation</li>
                  </ul>

                  <div className="warning-box">
                    <strong>Financial Warning:</strong> Never make financial decisions based solely on information 
                    from free online tools. Always consult qualified financial advisors.
                  </div>
                </div>
              </section>

              <section id="external-links" className="policy-section">
                <h2>9. External Links Disclaimer</h2>
                <div className="section-content">
                  <p>
                    Our website may contain links to external websites that are not provided or maintained by 
                    or in any way affiliated with D-Tech Studios. Please note that:
                  </p>
                  <ul>
                    <li>We do not guarantee the accuracy, relevance, timeliness, or completeness of any information on these external websites</li>
                    <li>The inclusion of any links does not necessarily imply a recommendation or endorsement of the views expressed within them</li>
                    <li>We have no control over the nature, content, and availability of those sites</li>
                    <li>Linking to external sites is at your own risk</li>
                  </ul>
                </div>
              </section>

              <section id="jurisdictional" className="policy-section">
                <h2>10. Jurisdictional Notice</h2>
                <div className="section-content">
                  <h3>10.1 Geographic Limitations</h3>
                  <p>
                    Our services are primarily intended for users in the United States. We make no claims that 
                    the website or any of its content is accessible or appropriate outside of the United States.
                  </p>

                  <h3>10.2 Legal Compliance</h3>
                  <p>
                    Access to the website may not be legal by certain persons or in certain countries. 
                    If you access the website from outside the United States, you do so on your own initiative 
                    and are responsible for compliance with local laws.
                  </p>

                  <h3>10.3 Governing Law</h3>
                  <p>
                    These disclaimers shall be governed by and construed in accordance with the laws of Texas, 
                    without regard to its conflict of law provisions.
                  </p>
                </div>
              </section>

              <section id="contact-us" className="policy-section">
                <h2>11. Contact Us</h2>
                <div className="section-content">
                  <p>If you have any questions about this Disclaimer, please contact us:</p>
                  <div className="contact-info">
                    <div className="contact-item">
                      <strong>Email:</strong> support@font-max.com
                    </div>
                    <div className="contact-item">
                      <strong>Address:</strong> 2568 Madison Ave, League City, TX 77573
                    </div>
                    <div className="contact-item">
                      <strong>Website:</strong> <a href="https://font-max.com">https://font-max.com</a>
                    </div>
                  </div>
                  <div className="note-box">
                    <strong>Legal Inquiries:</strong> For formal legal notices or official correspondence, 
                    please use our designated legal email address and include "Legal Notice" in the subject line.
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="policy-actions">
        <div className="container">
          <div className="actions-content">
            <h3>Understand Your Responsibilities</h3>
            <p>By using our services, you acknowledge that you have read and understood this Disclaimer and agree to its terms.</p>
            <div className="action-buttons">
              <a href="/TermsConditions" className="btn btn-primary">View Terms & Conditions</a>
              <a href="/Base64Tool" className="btn btn-secondary">Continue to Tools</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;